import { BaseDropDownControl } from "Controls/BaseDropDownControl";
import { ControlEventTypes } from "Core/Events/EventTypes";

import { IODataResult, IDataRow } from "Entities";
import { TextBlockCategory } from "Enums";
import { DataRequestOption } from "Service/Client";

/**
 * Control for selecting values from a dropdown list.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a ListControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class List extends BaseDropDownControl {
    private _kendoElement: kendo.ui.DropDownList;

    /**
     * @hidden
     */
    protected dmsControlClass = "dms-control-list";

    /**
     * @hidden
     */
    protected bindingProperty = "deferredValue";

    /**
     * @hidden
     */
    protected additionalBinding =
        this.controlDefinition.TextColumnName && this.controlDefinition.ValueColumnName != this.controlDefinition.TextColumnName
            ? "listText: " + this.controlDefinition.TextColumnName
            : "";
    private dataSourceOptions: kendo.data.DataSourceOptions;
    private data: IODataResult;

    private valueBeforeBinding: string;

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));

        this.dataSourceOptions = {
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    if (this.data != null) {
                        this.valueBeforeBinding = this._kendoElement.value();
                        (options.success as JQuery.Ajax.SuccessCallback<any>)(this.data, null, null);
                        return;
                    }
                },
            },
            schema: {
                data: function (data: IODataResult) {
                    return data.value[0];
                },
                total: function (data: IODataResult) {
                    return data["odata.count"];
                },
            },
        };

        let dropDownOptions = this.getDropDownOptions();

        let options: kendo.ui.DropDownListOptions = {
            dataValueField: this.controlDefinition.CommandValueColumnName,
            valueTemplate: `#= get("${this.controlDefinition.CommandTextColumnName}") || "" #`, // template for selected entry
            height: 290,
            autoBind: false,
            valuePrimitive: true,
            optionLabel: "",
            dataBound: (e: kendo.ui.DropDownListDataBoundEvent) => this.onDataBound(e),
            change: (e: kendo.ui.DropDownListChangeEvent) => this.onChange(e),
            noDataTemplate: dropDownOptions.noDataTemplate,
            autoWidth: this.controlDefinition.AutoWidth || false,
            footerTemplate: `<div class="dms-dropdown-footer k-widget k-header">#: instance.dataSource.total() ${
                this.controlDefinition.InsertNullValue || false ? "-1" : ""
            } # ${this.textBlockService.get("ComboboxItemsLabel", TextBlockCategory.global)}</div>`,
            headerTemplate: dropDownOptions.headerTemplate,
            template: dropDownOptions.template,
        };

        this._kendoElement = new kendo.ui.DropDownList(this.jQueryElement[0], options);
        this._kendoElement.popup.element.addClass("dms-control-dropdown-popup");

        if (dropDownOptions.setListWidth) {
            let listWidth = dropDownOptions.listWidth;
            if (listWidth > this._kendoElement.element.width()) {
                this._kendoElement.popup.element.width(listWidth);
            }
        }
    }

    /**
     * Format the value with highlighting from text search.
     * @param value Value to be formatted.
     * @hidden
     */
    public formatValue(value: string): string {
        return value;
    }

    /**
     * Get the selected value.
     * @since 4.0.0
     */
    public getValue(): string {
        let val = this._kendoElement.value();
        return val == "" ? null : val;
    }

    /**
     * Set the selected value.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: string) {
        let oldValue = this._kendoElement.value();
        if (oldValue != value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the index of the selected item.
     * @returns Zero-based index of the selected item.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.getSelectedIndex();
     * ```
     */
    public getSelectedIndex(): number {
        return this._kendoElement.select();
    }

    /**
     * Get the text of the selected item.
     * @since 4.0.0
     */
    public getText(): string {
        return this._kendoElement.text();
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Handler for change-event.
     * @param e EventArgs.
     */
    private onChange(e: kendo.ui.DropDownListChangeEvent): void {
        this.trigger(ControlEventTypes.Change);
    }

    /**
     * Handler for databound-event.
     * @param e EventArgs.
     */
    private onDataBound(e: kendo.ui.DropDownListDataBoundEvent): void {
        this.setBusy(false);

        if (this._kendoElement.select() == -1) {
            this._kendoElement.select(0);
            this._kendoElement.trigger("change");
            return;
        }
        if (this.valueBeforeBinding !== undefined && this.valueBeforeBinding != this._kendoElement.value()) {
            this._kendoElement.trigger("change");
        }

        this.trigger(ControlEventTypes.DataBound);
    }

    /**
     * Get info for requesting data.
     * @hidden
     */
    public getDataRequest(): DataRequestOption {
        let result: DataRequestOption = {
            ControlID: this.controlDefinition.ControlID,
            SearchValue: null,
            Take: null,
            Skip: null,
            FilterOperator: null,
            SearchText: null,
        };
        return result;
    }

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): Promise<void> {
        this.data = data;
        this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
        this.dataSource = this._kendoElement.dataSource;
        this._kendoElement.dataSource.read();
        return Promise.resolve();
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean): void {
        let element = this.getOuterElement().find(".k-button > .k-icon");
        if (isBusy) {
            element.empty();
            element.addClass("k-i-loading");
        } else {
            kendo.ui.icon(element, { icon: "caret-alt-down" });
            element.removeClass("k-i-loading");
        }
    }

    /**
     * Select the item at index.
     * @param index The zero-based index of the item which should be selected.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // in script:
     * @@ctr:1337@@.selectItemAt(5);
     *
     * // on page:
     * $dms(1337).selectItemAt(5);
     * ```
     */
    public selectItemAt(index: number): void {
        this._kendoElement.select(index);
        this._kendoElement.trigger("change");
    }

    /**
     * Returns true if an item with given value is in the dropdown list.
     * @param value The value to be searched.
     * @since 4.0.0
     * @returns True if the value is in the list of available values.
     * @example
     * ```JavaScript
     * @@ctr:1337@@.containsValue("1");
     * ```
     */
    public containsValue(value: string): boolean {
        let list = this._kendoElement.dataSource.data();
        let columnName = this.controlDefinition.CommandValueColumnName;
        for (let i = 0, len = list.length; i < len; i++) {
            if (list[i][columnName] == value) {
                return true;
            }
        }
        return false;
    }

    /**
     * Select the item which text contains given search string.
     * @param text The text to be searched.
     * @since 4.0.0
     */
    public selectItemWithText(text: string): void {
        let textColumnName = this.controlDefinition.CommandTextColumnName;
        this._kendoElement.select((dataItem: IDataRow) => {
            return dataItem[textColumnName] && dataItem[textColumnName].toString().indexOf(text) !== -1;
        });
    }

    /**
     * Reload the control items with data from the server.
     * @since 4.0.0
     */
    public reload(): Promise<void> {
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    /**
     * Reloads the data from the datasource and selects the value after the loading.
     * This is useful when you create a entry in an additional form and want to select the entry after saving the form.
     * @param value The value to be selected after reloading.
     * @since 4.0.0
     */
    public reloadAndSetValue(value: string): Promise<void> {
        this._kendoElement.dataSource.data([
            { [this._kendoElement.options.dataValueField]: value, [this._kendoElement.options.dataTextField]: "" },
        ]);
        this._kendoElement.value(value);
        return this.reload();
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        try {
            if (this._kendoElement != null) {
                this._kendoElement.destroy();
            }

            if (this.jQueryElement) {
                this.jQueryElement.off("focusout");
            }
        } catch (error) {}
    }
}
